import React, { useMemo } from 'react'
import { useHistory, Redirect, Link } from 'react-router-dom'
import { useQuery } from 'jsonapi-react'
import { Helmet } from 'react-helmet-async'
import { Layout, TwoColumns } from 'app/theme/layout'
import BackgroundCover from 'app/theme/BackgroundCover'
import Select from 'app/theme/forms/Select'
import Pagination from 'app/theme/Pagination'
import Breadcrumbs from 'app/theme/Breadcrumbs'
import NotFoundPage from 'app/pages/NotFoundPage'
import { getResizedPhotoUrl } from 'app/utils/photos'
import { useModalState } from 'lib/modal'
import locations from './locations'
import ListingFilterBar from './listingFilters/ListingFilterBar'
import heroImageBackground from 'assets/img/buy-a-food-truck.jpg'
import placeHolderImage from 'assets/svg/truck.svg'
import qs from 'qs'
import styles from './BuyTruckPage.module.css'
import 'react-input-range/lib/css/index.css'

const statusClass = {
  sold: styles['status-sold'],
  escrow: styles['status-escrow'],
  removed: styles['status-removed'],
}

const milesRadius = 200

const sortingOptions = [
  { label: 'Newest first', value: '' },
  { label: 'Oldest first', value: 'created_at' },
  { label: 'Price: highest first', value: '-price' },
  { label: 'Price: lowest first', value: 'price' },
  { label: 'Distance: closest first ', value: 'distance' },
  { label: 'Distance: farthest first', value: '-distance' },
]

const getLocationUrlSegment = (city, state) => {
  return city ? city.url_slug : state?.shortCode?.toLowerCase()
}

export default function BuyTruckPage(props) {
  const {
    city,
    stateSlug,
    vehicleType,
    page
  } = props
  const history = useHistory()

  const pageSize = 99

  const params = useMemo(() => {
    const { sort, filter } = qs.parse(history.location.search.substr(1))
    return {
      vehicle_state: city ? undefined : stateSlug,
      page: page || 1,
      vehicle_type: (vehicleType)?.split('-') || ['cart', 'trailer', 'truck'].reverse(),
      sort: sort,
      filters: filter ? JSON.parse(filter) : {},
    }
  }, [])

  const getPageUrl = params => {
    const path = [
      '/marketplace/buy-a-food-truck',
      params?.vehicle_state || city?.url_slug,
      params.vehicle_type.length < 3 ? params?.vehicle_type?.join('-') : undefined,
      params?.page && params?.page !== 1 ? params?.page : undefined,
    ].filter(Boolean).join('/')

    let search = {}

    if (params?.sort) {
      search.sort = params.sort
    }

    if (params?.filters && Object.keys(params?.filters).length) {
      search.filter = JSON.stringify(params?.filters, (k,v) => {
        if (v === '') {
          return undefined
        }
        if (typeof v === 'string') {
          return encodeURIComponent(v)
        }
        if (Array.isArray(v) && !v.length) {
          return undefined
        }
        return v
      })

      if (search.filter === '{}') {
        delete search.filter
      }
    }

    search = qs.stringify(search, { encode: false })

    return `${path}/${search ? `?${search}` : ''}`
  }

  const updatePageUrl = param => {
    params.page = 1
    window.location = getPageUrl(params)
  }

  const { data, meta, isLoading, error, errors } = useQuery(['market_listings', {
    include: [
      'photo',
    ],
    geolocation: city ? [city?.location.lng, city?.location.lat] : undefined,
    sort: (city ? params.sort : (params.sort !== 'distance' && params.sort !== '-distance' ? params.sort : undefined)) || '-created_at',
    page: {
      size: pageSize,
      number: params.page,
    },
    stats: {
      total: 'count',
    },
    fields: {
      market_listings: [
        'slug',
        'title',
        'description',
        'price',
        'vehicle_city',
        'vehicle_state',
        'status',
        'vehicle_type',
        'additional_equipment'
      ],
      photos: [
        'uuid',
        'extension',
        'path',
      ],
    },
    extra_fields: city ? { market_listings: ['distance'] } : undefined,
    filter: {
      search: params.filters.search,
      vehicle_state: params.vehicle_state,
      distance: city ? { lte: milesRadius } : undefined,
      price: { gte: params.filters.min_price, lte: params.filters.max_price },
      vehicle_type: params.vehicle_type,
      fuel_type: params.filters.fuel_type,
      mileage: { gte: params.filters.min_mileage, lte: params.filters.max_mileage },
      year: { gte: params.filters.min_year, lte: params.filters.max_year },
      vehicle_feature_id: params.filters.equipment,
      status: [
        'new',
        'live',
        'available',
        'sold',
      ],
    }
  }])

  const filtersCount = useMemo(() => ({
    full: Object.keys(params.filters).filter(k => ['search'].indexOf(k) < 0).length + (params.vehicle_state ? 1 : 0),
    partial: Object.keys(params.filters).filter(k => ['search', 'min_price', 'max_price'].indexOf(k) < 0).length
  }), [])

  const state = city ? city : (params.vehicle_state
    ? locations.find(l => l.shortCode === params.vehicle_state.toUpperCase())
    : undefined)

  const { isOpen, openModal, closeModal } = useModalState()

  const singleTerm = params.vehicle_type && params.vehicle_type.length === 1
  const titleCaseVehicleTypes = params.vehicle_type?.map(
    t => t.charAt(0).toUpperCase() + t.slice(1)
  )
  const lastVehicleType = !singleTerm && params.vehicle_type?.length ? titleCaseVehicleTypes.pop() : undefined
  const metaString = params.vehicle_type.length === 3
    ? 'Truck, Cart & Trailer'
    : titleCaseVehicleTypes.join(', ') +
      `${lastVehicleType ? ` and ${lastVehicleType}` : ''}`

  const metaStringPlural = params.vehicle_type.length === 3
  ? 'Trucks, Carts & Trailers'
  : titleCaseVehicleTypes.map(t => t + 's').join(', ') +
    `${lastVehicleType ? ` and ${lastVehicleType}s` : ''}`

  const metaTitle = `Food ${metaStringPlural} for Sale ${state ? `in ${state.name}` : ''} - Mobile Food Alliance`

  const metaDescription = `Find new and used food ${metaStringPlural.toLowerCase()} for sale near you ${state ? `in ${state.name}` : ' all over the US & Canada'}. Sort by price, location, equipment and more!`

  const metaKeywords = `${state ? `${state.name},` : ''}food truck for sale,food trucks,food carts,concession trailers,for sale,used food trucks`

  const crumbs = [
    { text: 'Home', url: `/` },
    { text: 'Marketplace', url: '/marketplace/' },
  ]

  if (state) {
    crumbs.push(
      { text: 'Buy a Food Truck', url: '/marketplace/buy-a-food-truck/' },
      { text: `${state.name} Food Trucks for Sale`, url: `/marketplace/buy-a-food-truck/${getLocationUrlSegment(city, state)}/` },
      { text: `${metaStringPlural} for Sale` }
    )
  } else {
    crumbs.push(
      { text: 'Buy a Food Truck', url: '/marketplace/buy-a-food-truck/' },
      { text: `${metaStringPlural} for Sale` }
    )
  }

  const googleMetaTags = JSON.stringify({
    '@context': 'http://schema.org',
    '@type': 'OfferCatalog',
    "@id": "https://mobilefoodalliance.com/marketplace/buy-a-food-truck/#OfferCatalog",
    url: `https://mobilefoodalliance.com/marketplace/buy-a-food-truck/${getLocationUrlSegment(city, state) ? getLocationUrlSegment(city, state) + '/' : ''}`,
    numberOfItems: meta?.stats?.total?.count,
    itemListOrder: "ItemListUnordered",
    description: `Looking for a new or used food ${metaString.toLowerCase()} for sale? Find the food service equipment that's right for you.`,
    name: "Food Trucks, Carts & Trailers For Sale",
    itemListElement: data?.map((truck, index) => ({
      '@type': 'Product',
      '@id': `https://mobilefoodalliance.com/marketplace/buy-a-food-truck/listing/${truck.slug}/#Product`,
      image: truck.photo?.url || placeHolderImage,
      url: `https://mobilefoodalliance.com/marketplace/buy-a-food-truck/listing/${truck.slug}/`,
      name: truck.title,
      position: index,
      offers: {
        '@type': 'Offer',
        price: truck.price,
        priceCurrency: 'USD',
        url: `https://mobilefoodalliance.com/marketplace/buy-a-food-truck/${getLocationUrlSegment(city, state) ? getLocationUrlSegment(city, state) + '/' : ''}`,
      },
    })),
  })

  const pageOutOfRange = params.page > 1 && Math.ceil(meta?.stats?.total?.count / pageSize) < params.page

  if (isLoading) {
    return null
  }

  if (pageOutOfRange) {
    return <NotFoundPage />
  }

  if (city && !city?.major) {
    // redirect to the state page for this minor city
    return <Redirect to={`/marketplace/buy-a-food-truck/${city?.region?.code.toLocaleLowerCase()}/`} />
  }

  return (
    <Layout>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />
        <meta name="og:title" content={metaTitle} />
        { city && !city?.major && (
          <meta name="robots" content="noindex" />
        )}
        <script type="application/ld+json">
          {googleMetaTags}
        </script>
      </Helmet>

      <BackgroundCover src={heroImageBackground}>
        <div className="w-full">
          <div className={styles.heroContent}>
            <h1 className="font-roboto-flex  text-3xl lg:text-5xl text-center leading-tight mb-3 text-white">
              {`Food ${metaStringPlural} For Sale ${state ? `in ${state.name}` : ''}`}</h1>
            <p className="font-roboto font-light text-xl lg:text-3xl text-center leading-tight mb-5 m-auto text-white max-w-2xl">
              Search below for our entire collection of gourmet food trucks for sale, food carts for sale, and concession trailers for sale{state ? ` in ${state.name}` : ''}.
            </p>
          </div>
        </div>
      </BackgroundCover>

      <div className="container mt-12 mb-10">
        <p className="font-light text-gray-900 text-3xl text-center leading-tight mb-5">
          Looking for a new or used food {metaString.toLowerCase()} for sale? Find the food service equipment that's right for you.
        </p>
        <p className="font-light text-gray-700 text-xl leading-normal mb-5">
          Whether you're looking to purchase your first food truck or expand your fleet,
          our selection of food trucks for sale, food carts for sale, and trailers for sale
          is sure to help you get rolling. Use the Filters to search by location to find
          food trucks for sale near you, by price, by new truck listings, and more.

          Want to learn more about how our process works? Check out our <a className="font-roboto text-teal" href="/marketplace/faq/" target="_blank"> FAQ page for more details</a>.
        </p>
        <p className="font-light text-gray-700 text-md italic leading-relaxed mb-5">
          *NOTE: The listings below are by independent sellers and NOT owned by Mobile Food Alliance. Make sure to examine all equipment, including getting a professional evaluation,
          before making any purchase or transaction.
        </p>
      </div>

      <ListingFilterBar
        params={params}
        updatePageUrl={updatePageUrl}
        isOpen={isOpen}
        closeModal={closeModal}
        stateSlug={params.vehicle_state}
        citySlug={params.citySlug}
        filtersCount={filtersCount}
      />

      <div className="container mt-5">
        <Breadcrumbs
          crumbs={crumbs}
        />
        <TwoColumns sidebar={<Sidebar params={params} city={city} />}>
          <div className="flex flex-col md:flex-row mt-5">
            <h2 className="flex-1 text-3xl lg:text-4xl text-gray-900 font-roboto-flex leading-tight">
              Food {metaStringPlural} for Sale
            </h2>
            <Select
              label=""
              value={params.sort}
              options={city ? sortingOptions : sortingOptions.filter(i => i.value !== 'distance' && i.value !== '-distance')}
              classNames={{
                select: "select-basic inline w-auto",
                div: "my-2 md:my-0 block md:inline",
              }}
              labelProps={{
                className: "inline px-2"
              }}
              onChange={value => {
                params.sort = value || undefined
                updatePageUrl()
              }}
            />
          </div>
          <Listings
            params={params}
            page={params.page}
            pageSize={pageSize}
            getPageUrl={getPageUrl}
            data={data}
            meta={meta}
            state={state}
            metaStringPlural={metaStringPlural}
            isLoading={isLoading}
          />
        </TwoColumns>
      </div>

      <div className="fixed w-full bottom-0 pb-2 flex justify-center md:hidden">
        <button
          children={ `Filters ${filtersCount.full > 0 ? `(${filtersCount.full})` : "" } `}
          className="btn btn-navy rounded-full shadow text-lg px-8 py-2"
          onClick={() => {
            // open the modal
            openModal()
          }}
        />
      </div>
    </Layout>
  )
}

function Sidebar(props) {
  const {
    city,
    params
  } = props

  return <div className="pt-5">
    <h2 className="font-roboto-flex text-xl text-gray-900 leading-none">How It Works</h2>
    <ul className="list-disc list-inside font-light mt-2 mb-5">
      <li>Find the mobile food equipment you like</li>
      <li>Inquire about the equipment and place your refundable deposit</li>
      <li>We’ll connect you with the seller and help you along the way</li>
    </ul>
    <p className="font-light mt-2 mb-5">Read our <a href="/marketplace/faq/" className="font-roboto text-teal">FAQs for more information</a>.</p>
    <hr className="my-8"/>
    <h2 className="font-roboto-flex text-xl text-gray-900 leading-none">Build Your Perfect Food Truck, Trailer, or Cart</h2>
    <p className="font-light mt-2 mb-5">Build your menu. Determine equipment. Decide on your budget.</p>
    <a className="font-roboto btn btn-transparent px-5 py-2 rounded" href="/marketplace/build-a-food-truck/">Get Rolling</a>
    <hr className="my-8"/>
    <h2 className="font-roboto-flex text-xl text-gray-900 leading-none">Looking to Lease or Rent?</h2>
    <p className="font-light mt-2 mb-5">Get the support you need building a food truck business from the ground up.</p>
    <a className="font-roboto btn btn-transparent px-5 py-2 rounded" href="/marketplace/lease-a-food-truck/">Get Started</a>
    <hr className="my-8"/>
    <h2 className="font-roboto-flex text-xl text-gray-900 leading-none">Resources</h2>
    <ul className="list-none list-inside font-light mt-2 mb-5">
      <li className="mb-2"><a target="_blank" className="font-roboto" href="https://mobilefoodalliance.com/blog/how-much-does-a-food-truck-cost/">The Real Cost to Buy a Food Truck</a></li>
      <li className="mb-2"><a target="_blank" className="font-roboto" href="https://mobilefoodalliance.com/blog/what-i-wish-id-known-before-starting-my-food-truck/">50 Food Truck Owners Give Advice: What to Know Before Starting Your Food Truck</a></li>
      <li className="mb-2"><a target="_blank" className="font-roboto" href="https://mobilefoodalliance.com/blog/start-food-truck-learn-commissaries/">Food Truck Commissaries: What You Need To Know</a></li>
    </ul>
    <NearbyCitySidebar city={city} params={params} />
  </div>
}

function NearbyCitySidebar(props) {
  const {
    city,
    params
  } = props

  const query = useQuery([
    'cities',
    {
      fields: {
        cities: [
          'name',
          'url_slug',
        ]
      },
      filter: {
        region_code: (city?.url_slug?.substr(-2,2) || params?.vehicle_state || '').toUpperCase() || undefined,
        major: true,
        market: true,
        active: true
      },
      sort: 'id',
      page: {
        size: 20,
        number: 1,
      },
    },
  ])

  if (query.isLoading || query.error) {
    return null
  }

  return (
    <>
      <hr className="my-8"/>
      <h2 className="font-roboto-flex text-xl text-gray-900 leading-none">Food Trucks for Sale{(city?.url_slug?.substr(-2,2) || params?.vehicle_state) ? ' Near You' : ''}</h2>
      <ul className="list-none list-inside font-light mt-2 mb-5">
      {query.data.map(city => (
        <li key={city.id} className="mb-2">
          <a
            href={`/marketplace/buy-a-food-truck/${city.url_slug}/`}
            className="hover:text-blue font-roboto font-light"
          >
            Food Trucks for Sale in {city.name}
          </a>
        </li>
      ))}
      </ul>
    </>
  )
}

function Listings(props) {
  const {
    params,
    page,
    pageSize,
    getPageUrl,
    data,
    meta,
    state,
    metaStringPlural,
    isLoading
  } = props

  return (
    <div className="md:container">
      {data?.length ? (
        <div className="mt-5 md:mt-10 mb-2">
          {data.map(truck => (
            <Listing key={truck.id} data={truck} />
          ))}
        </div>
      ) : isLoading ? (
        <>Loading...</>
      ) : (
        <div className="text-center my-10">
          <p className="text-xl italic">
            Sorry, we don't have any {metaStringPlural || 'listings'} for sale {state ? `in ${state.name}` : '' } at the moment.
            Go to our <Link className="font-roboto text-blue" to="/marketplace/buy-a-food-truck/">General Buy a Food Truck page</Link> to search other locations.
          </p>
        </div>
      ) }
      {meta && (
        <Pagination
          className="justify-center my-10"
          total={meta.stats.total.count}
          limit={pageSize}
          current={page}
          getLinkProps={({ page }) => {
            params.page = page
            return {
              href: getPageUrl(params)
            }
          }}
        />
      )}
    </div>
  )
}

function Listing({ data }) {
  const image = data.photo ? getResizedPhotoUrl(data.photo, 365, 365) : placeHolderImage

  return (
    <div className="w-full bg-white shadow rounded p-4 md:p-6 my-5">
      <div className="flex mb-4 md:mb-0">

        <div className={["w-1/4 md:w-2/5 relative h-0 overflow-hidden rounded", styles.truckBoxImage]}>
          <img
            src={image}
            loading="lazy"
            alt={data.title}
            className="lazy-img absolute inset-0 w-full rounded"
            width="300"
            height="180"
          />
        </div>


        <div className={["flex flex-col flex-1 w-3/4 md:w-3/5 pl-5 md:px-5", styles.text_container]}>
          <div className="flex-1">

            <h3 className={["text-gray-900 font-roboto-flex text-xl mb-2 leading-tight overflow-hidden", styles.lineClamp]}>{data.title}</h3>

            {data.additional_equipment && (
              <p className={["font-light mb-2 overflow-hidden", styles.lineClamp]}>{data.additional_equipment}</p>
            )}
            <div className="flex items-center">
              <p className="flex-1 text-xl text-aegean font-light mb-0 md:mb-5">${data.price.toLocaleString('en-US', { maximumFractionDigits: 2 })}</p>
              <div className="text-right md:hidden">
                <p className="uppercase">{data.vehicle_city}, {data.vehicle_state}</p>
                <p className="text-sm capitalize">Food {data.vehicle_type}</p>
              </div>
            </div>
          </div>

          <div className="flex">
            <div className="hidden md:block">
              <a
                className="btn btn-aegean font-roboto px-5 py-2 rounded"
                href={`/marketplace/buy-a-food-truck/listing/${data.slug}/`}
              >
                View Details
              </a>
            </div>

            <div className="flex-1 text-right hidden md:block">
              <p className="uppercase">{data.vehicle_city}, {data.vehicle_state}</p>
              {data.distance && (
                <p className="text-sm">About {Math.round(data.distance)} miles away</p>
              )}
              <p className="text-sm capitalize">Food {data.vehicle_type}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="md:hidden w-full">
        <a
          className="btn btn-aegean font-roboto px-5 py-2 rounded w-full text-center"
          href={`/marketplace/buy-a-food-truck/listing/${data.slug}/`}
        >
          View Details
        </a>
      </div>
    </div>
  )
}
